import { Box } from '@wix/design-system'
import React, { FC } from 'react'

import { Button } from '../Button'
import { FancyAutoComplete } from '../FancyAutoComplete'
import { FooterControls, FooterControlsSpacerHeight, FooterControlsType } from '../FooterControls'
import { Heading } from '../Heading/Heading'
import { LogoWithBack } from '../LogoWithBack'
import { VideoBox } from '../VideoBox/VideoBox'
import { VideoLayout } from '../VideoLayout/VideoLayout'
import { WixIntrosTemplate } from '../WixIntrosTemplate'
import { BusinessTermService } from './api/BusinessTermService'
import { AutoCompleteWithOverrides } from './AutoCompleteWithOverrides'
import { dataHooks } from './Category.dataHooks'
import s from './Category.scss'
import { CategoryLayout } from './constants'
import { Suggestions } from './Suggestions'
import { useCategory } from './useCategory'

export type CategoryProps = {
  businessTermService: BusinessTermService
  layout?: CategoryLayout
}

export const Category: FC<CategoryProps> = props => {
  const {
    autoCompleteRef,
    categoriesOptions,
    handlers: {
      handleClear,
      handleSearchSelect,
      handleInputClick,
      handleSearchChange,
      handleNextClick,
      handleSuggestionClick,
      handleBackClick,
      handleSkipClick,
    },
    state: { search = '', category, clickedButton, categoriesLoading },
    content: {
      placeholder,
      isAddUserInputAsOption,
      title,
      video: contentVideo,
      hint,
      suggestionsLabel,
      suggestions,
      nextButton,
      backButton,
      skipButton,
    },
    layout,
    selectedId,
    isLargeDesktopScreen,
  } = useCategory(props)

  const autocomplete = (
    <>
      <Box gap="SP3" verticalAlign="bottom">
        <Box direction="vertical" flexGrow="1">
          <FancyAutoComplete
            ref={autoCompleteRef}
            value={search || ''}
            selectedId={selectedId}
            onChange={handleSearchChange}
            status={categoriesLoading ? 'loading' : undefined}
            options={categoriesOptions}
            placeholder={placeholder}
            size="large"
            onSelect={handleSearchSelect}
            onInputClicked={handleInputClick}
            maxHeightPixels="300"
            className={s.autoComplete}
            border="bottomLine"
            onClear={categoriesLoading ? undefined : handleClear}
            customRenderer={AutoCompleteWithOverrides}
            isIconHidden={isAddUserInputAsOption}
            dataHook={dataHooks.autoComplete()}
          />
        </Box>
        <Button
          skin={layout === CategoryLayout.WIX_INTROS ? 'standard' : undefined}
          disabled={!category}
          size={isLargeDesktopScreen ? 'large' : 'medium'}
          onClick={handleNextClick}
          label={nextButton}
          isLoading={clickedButton === 'primary'}
          dataHook={dataHooks.nextButton()}
        />
      </Box>
      <Suggestions
        visible={!category}
        label={suggestionsLabel}
        items={suggestions.items}
        onItemClick={handleSuggestionClick}
      />
    </>
  )

  const mainContent = (
    <Box direction="vertical" className={s.autoCompleteContainer}>
      <Box marginBottom="SP5">
        <Heading size="extraLarge" className={s.title} dataHook={dataHooks.title()}>
          {title}
        </Heading>
      </Box>
      {autocomplete}
    </Box>
  )

  const video =
    (contentVideo && hint && <VideoBox backgroundColor="D70" src={contentVideo} description={hint} />) || null

  const footerControls = (
    <FooterControls
      backText={backButton}
      backOnClick={handleBackClick}
      skipText={skipButton}
      skipOnClick={handleSkipClick}
      type={FooterControlsType.TRANSPARENT}
      spacerHeight={FooterControlsSpacerHeight.NONE}
      isVisible
    />
  )

  // `layout`-prop solution is good enough.
  // Until we get more requirements on BI events for Wix Intros Category page
  // reusing existing events/events names is a good solution.
  if (layout === CategoryLayout.WIX_INTROS) {
    return (
      <WixIntrosTemplate withLogo>
        <Heading size="xxl" className={s.title} dataHook={dataHooks.title()}>
          {title}
        </Heading>
        <Box direction="vertical" width="100%" maxWidth="500px" marginTop="SP6">
          {autocomplete}
        </Box>
        {footerControls}
      </WixIntrosTemplate>
    )
  }

  if (layout === CategoryLayout.NEW_CONCEPT) {
    return (
      <Box height="100vh" overflow="hidden">
        <LogoWithBack />
        <VideoLayout mainContent={mainContent} video={video} />
        {footerControls}
      </Box>
    )
  }

  return null
}
